package dev.lingvo.site.api

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import dev.lingvo.site.i18n.tr
import kotlinx.coroutines.CancellationException

sealed class Async<out T> {
    data object Loading : Async<Nothing>()
    data class Success<T>(val value: T) : Async<T>()
    /**
     * A load that did not arrive, and — where the server said so — what it answered.
     *
     * **[status] exists because "we couldn't read your plan" is the wrong sentence for a refusal.**
     * Several surfaces are specified with distinct states for 403 and 404: a translator opening a
     * billing route should be told the numbers are for owners and admins, and a deleted project
     * should say it is gone. Without the code, every one of those collapsed to the same red alert
     * with a Retry button that will fail identically — which reads as a broken product rather than
     * as a boundary, and invites the reader to keep pressing it.
     *
     * Null for a failure that never reached the server at all: a dropped connection, a DNS failure,
     * a request cancelled by a navigation. Those genuinely have no status, and inventing 0 or 500
     * for them would send a caller down a branch about the server when the server was never asked.
     */
    data class Failure(
        val message: String,
        val status: Int? = null,
        /**
         * The server's own name for what went wrong — `provider_disabled`, `note_required`.
         *
         * Carried because [message] is a sentence and this is an identifier, and only an identifier
         * can be translated. The server writes 155 of these sentences and every one of them reaches
         * a reader in English, whatever language the interface is in; a stable code is the hook a
         * catalogue entry can hang on, with the server's own wording as the fallback until one
         * exists. Null for anything that never reached the server — a dropped socket has no code
         * any more than it has a status.
         */
        val code: String? = null,
    ) : Async<Nothing>() {
        /** The reader is not allowed to see this, as distinct from it not being there. */
        val isRefusal: Boolean get() = status == 401 || status == 403

        /** It is not there — deleted, or never existed under this id. */
        val isMissing: Boolean get() = status == 404

        /**
         * The refusal in the reader's language, falling back to the server's own words.
         *
         * **A catalogue entry per error code, and the server's English until there is one.** The
         * server writes its refusals as sentences — "Say what needs changing. A rejection without a
         * reason is not review." — and they are good sentences, but they are English sentences and
         * they reach a reader whose entire interface is in German. [code] is the only stable thing
         * in the pair, so it is what a translation keys on.
         *
         * Nothing changes until a `api.error.<code>` entry exists, which is the point: this is a
         * hook, not a rewrite. A team can translate the refusals people actually hit and leave the
         * operator-facing ones in English, rather than facing all 155 at once.
         *
         * **A generic code is not a specific error, and must never be translated.** More than half
         * of the server's refusals — 216 of 393 — reach the code-less overload and get one of
         * `bad_request`, `not_found`, `conflict`, `forbidden`, `unauthorized` or
         * `payload_too_large`. Those say only *which kind* of refusal it was; the sentence beside
         * them is the whole content. Looking one up would let a single catalogue entry for
         * `api.error.bad_request` replace 216 distinct refusals with the same sentence, and the
         * more carefully somebody translated it the more thoroughly it would destroy the message it
         * replaced. They are skipped, and their English stands.
         *
         * **Server messages that interpolate a value still need work this cannot do.** Around a
         * third read `"Unknown provider mode: $value."`, and a translation of that needs the
         * parameterised form the interface already has — `{value}` and an argument — which means
         * changing them where they are written. Recorded in `docs/INTERFACE-CRAFT.md` rather than
         * half-done here, because a catalogue entry that silently drops the value is worse than the
         * English sentence that keeps it.
         */
        fun readable(): String =
            code?.takeIf { it !in GENERIC_ERROR_CODES }
                ?.let { tr("api.error.$it", message) }
                ?: message
    }
}

/**
 * The codes that mean "no specific code", which is the opposite of something to translate.
 *
 * `Errors.kt` gives every refusal a code, but the code-less overloads supply one of these — they
 * name the HTTP outcome, not the reason. The reason lives entirely in the message beside them.
 */
private val GENERIC_ERROR_CODES = setOf(
    "bad_request",
    "unauthorized",
    "forbidden",
    "not_found",
    "conflict",
    "payload_too_large",
)

/**
 * A value that can be re-fetched without throwing away what it is already showing.
 *
 * @param refreshing a re-fetch is in flight over a value that is still on screen. Distinct from
 *   [Async.Loading], which means there is nothing to show at all.
 */
data class Reloadable<T>(
    val state: Async<T>,
    val refreshing: Boolean,
    /** A failed refresh over a still-visible success. Initial-load failures remain in [state]. */
    val refreshError: Async.Failure? = null,
)

/**
 * [loadAsync], except that reloading a settled value keeps it visible.
 *
 * **The distinction the plain version cannot make.** `loadAsync` re-keys its `remember` on every
 * key, so passing a refresh counter as a key discards the value before the request is even sent:
 * the reader presses refresh and the page they were reading is replaced by skeleton bars, then
 * reassembles with no indication that anything changed. On a page whose whole purpose is to be
 * looked at after a `lingvo push`, that is worse than the browser reload it replaced.
 *
 * [keys] identify the subject — change one and the old value is genuinely wrong, so it goes.
 * [reloadToken] re-runs the request against the same subject and keeps the last good value on
 * screen while it does.
 */
@Composable
fun <T> loadReloadable(
    vararg keys: Any?,
    reloadToken: Int,
    block: suspend () -> T,
): Reloadable<T> {
    var state by remember(*keys) { mutableStateOf<Async<T>>(Async.Loading) }
    var refreshing by remember(*keys) { mutableStateOf(false) }
    var refreshError by remember(*keys) { mutableStateOf<Async.Failure?>(null) }
    val currentBlock by rememberUpdatedState(block)

    LaunchedEffect(*keys, reloadToken) {
        // Blank the surface only when there is nothing worth keeping. A refresh failure stays
        // beside the last success; an initial failure is replaced by the retry attempt.
        val previousSuccess = state as? Async.Success<T>
        if (previousSuccess != null) refreshing = true else state = Async.Loading
        refreshError = null
        val result: Async<T> = try {
            Async.Success(currentBlock())
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            Async.Failure(
                message = failure.message?.takeIf(String::isNotBlank)
                    ?: "Couldn't load this content. Retry?",
                status = (failure as? ApiException)?.status,
                code = (failure as? ApiException)?.code,
            )
        }
        if (result is Async.Failure && previousSuccess != null) {
            refreshError = result
            state = previousSuccess
        } else {
            state = result
        }
        refreshing = false
    }
    return Reloadable(state, refreshing, refreshError)
}

@Composable
fun <T> loadAsync(
    vararg keys: Any?,
    block: suspend () -> T,
): Async<T> {
    var state by remember(*keys) { mutableStateOf<Async<T>>(Async.Loading) }
    val currentBlock by rememberUpdatedState(block)

    LaunchedEffect(*keys) {
        state = Async.Loading
        state = try {
            Async.Success(currentBlock())
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            // The status travels when there is one. `ApiException` already carries it; anything else
            // — a dropped socket, a cancelled navigation — never reached the server and gets null.
            Async.Failure(
                message = failure.message?.takeIf(String::isNotBlank)
                    ?: "Couldn't load this content. Retry?",
                status = (failure as? ApiException)?.status,
                code = (failure as? ApiException)?.code,
            )
        }
    }
    return state
}
